import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Container,
  Row,
  Table,
  Button as BootstrapButton,
} from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../components/ui/DateInput";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import warehouseIcon from "../../media/icons8-hierarchy.png";
import GetData from "../../services/getData";
import filterIcon from "../../media/filter_icon.png";

function WarehouseLogs() {
  const { t } = useTranslation("warehouse");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedService, setSelectedService] = useState("all");
  const [selectedDirection, setSelectedDirection] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  useEffect(() => {
    document.title = t("logs.title");
  }, [t]);

  const getFieldValue = (field, fallback = "") => {
    if (field && typeof field === "object" && "value" in field) {
      return field.value;
    }
    return field ?? fallback;
  };

  const [users, setUsers] = useState([]);

  const loadLogs = async () => {
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "warehouse",
        block: "logs",
        search: searchTerm.trim(),
        id_warehouse: selectedWarehouse === "all" ? "" : selectedWarehouse,
        id_service: selectedService === "all" ? "" : selectedService,
        direction: selectedDirection === "all" ? "" : selectedDirection,
        id_user: selectedUser === "all" ? "" : selectedUser,
        date_from: dateFrom || "",
        date_to: dateTo || "",
        page: pagination.page,
        limit: pagination.limit,
      });

      const result = data.result || data.request?.result || [];
      const warehousesData =
        data.warehouses || data.request?.warehouses || [];
      const servicesData = data.services || data.request?.services || [];
      const usersData = data.users || data.request?.users || [];
      const paginationData =
        data.pagination || data.request?.pagination || null;

      setItems(result);
      setWarehouses(warehousesData);
      setServices(servicesData);
      setUsers(usersData);

      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          page: paginationData.page ?? prev.page,
          limit: paginationData.limit ?? prev.limit,
          total: paginationData.total ?? prev.total,
          pages: paginationData.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Ошибка загрузки истории склада:", err);
      setError(err.message || t("logs.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      loadLogs();
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    selectedWarehouse,
    selectedService,
    selectedDirection,
    selectedUser,
    dateFrom,
    dateTo,
    pagination.page,
  ]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDirectionChange = (e) => {
    setSelectedDirection(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUserChange = (e) => {
    setSelectedUser(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedWarehouse("all");
    setSelectedService("all");
    setSelectedDirection("all");
    setSelectedUser("all");
    setDateFrom("");
    setDateTo("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const renderPageButtons = () => {
    if (totalPages <= 1) return null;
    if (totalPages < 5) {
      return Array.from({ length: totalPages }, (_, idx) => {
        const pageNum = idx + 1;
        return (
          <BootstrapButton
            key={pageNum}
            variant={pageNum === pagination.page ? "primary" : "outline-secondary"}
            size="sm"
            className="mx-1"
            onClick={() => handlePageClick(pageNum)}
          >
            {pageNum}
          </BootstrapButton>
        );
      });
    }
    const currentPage = pagination.page;
    let start = Math.max(1, currentPage - 1);
    let end = start + 2;
    if (end > totalPages) {
      end = totalPages;
      start = end - 2;
    }
    const buttons = [];
    if (start > 1) {
      buttons.push(
        <BootstrapButton
          key="left-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(start - 1)}
        >
          ...
        </BootstrapButton>,
      );
    }
    for (let p = start; p <= end; p++) {
      buttons.push(
        <BootstrapButton
          key={p}
          variant={p === currentPage ? "primary" : "outline-secondary"}
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(p)}
        >
          {p}
        </BootstrapButton>,
      );
    }
    if (end < totalPages) {
      buttons.push(
        <BootstrapButton
          key="right-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(end + 1)}
        >
          ...
        </BootstrapButton>,
      );
    }
    return buttons;
  };

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={warehouseIcon}
                title={t("logs.title")}
                showStatus={false}
                status=""
              />
            }
            buttons={[]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <label>{t("filters.searchLabel", { ns: "common" })}</label>
                    <input
                      type="text"
                      placeholder={t("logs.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <label>{t("logs.warehouseLabel")}</label>
                      <select
                        value={selectedWarehouse}
                        onChange={handleWarehouseChange}
                      >
                        <option value="all">{t("shared.allWarehouses")}</option>
                        {warehouses.map((warehouse) => (
                          <option
                            key={warehouse?.id?.value || warehouse?.id}
                            value={warehouse?.id?.value || warehouse?.id}
                          >
                            {warehouse?.name?.value || warehouse?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>{t("logs.serviceLabel")}</label>
                      <select
                        value={selectedService}
                        onChange={handleServiceChange}
                      >
                        <option value="all">{t("shared.allServices")}</option>
                        {services.map((service) => (
                          <option
                            key={service?.id?.value || service?.id}
                            value={service?.id?.value || service?.id}
                          >
                            {service?.name?.value || service?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>{t("logs.directionLabel")}</label>
                      <select
                        value={selectedDirection}
                        onChange={handleDirectionChange}
                      >
                        <option value="all">{t("shared.all")}</option>
                        <option value="1">{t("logs.income")}</option>
                        <option value="0">{t("logs.writeoff")}</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>{t("logs.userLabel")}</label>
                      <select
                        value={selectedUser}
                        onChange={handleUserChange}
                      >
                        <option value="all">{t("logs.allUsers")}</option>
                        {users.map((user) => (
                          <option
                            key={user?.id?.value || user?.id}
                            value={user?.id?.value || user?.id}
                          >
                            {user?.name?.value || user?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group filter-range">
                      <label>{t("logs.dateLabel")}</label>
                      <div className="filter-range-inputs">
                        <DateInput
                          value={dateFrom}
                          onChange={handleDateFromChange}
                        />
                        <span className="range-separator">—</span>
                        <DateInput
                          value={dateTo}
                          onChange={handleDateToChange}
                        />
                      </div>
                    </div>
                    <button
                      className="button-default light"
                      onClick={clearFilters}
                    >
                      {t("filters.reset", { ns: "common" })}
                    </button>
                  </div>
                </div>
              </div>

              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : items.length === 0 ? (
                  <div className="no-patients">{t("logs.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("logs.colDate")}</th>
                        <th>{t("logs.colWarehouse")}</th>
                        <th>{t("logs.colService")}</th>
                        <th>{t("logs.colUnit")}</th>
                        <th>{t("logs.colDirection")}</th>
                        <th>{t("logs.colCount")}</th>
                        <th>{t("logs.colUser")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={item?.id?.value || item?.id}
                          className="table-item-link"
                        >
                          <td>{getFieldValue(item?.date, "")}</td>
                          <td>{getFieldValue(item?.warehouse_name, "")}</td>
                          <td>{getFieldValue(item?.service_name, "")}</td>
                          <td>{getFieldValue(item?.unit_name, "")}</td>
                          <td>{getFieldValue(item?.direction_name, "")}</td>
                          <td>{getFieldValue(item?.count, 0)}</td>
                          <td>{getFieldValue(item?.user_name, "")}</td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={7} className="summary-cell">
                          {t("shared.foundRecords", { count: pagination.total || items.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {items.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("shared.pageOf", { page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default WarehouseLogs;

